import { useState } from "react";
import { Loader2, X, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import apiClient, { formatApiErrorDetail } from "@/lib/api";
import StarRating from "@/components/review/StarRating";
import { RATING_CATEGORIES } from "@/components/review/reviewMeta";

const EMPTY = {
  overall_rating: 0,
  cleanliness_rating: 0,
  location_rating: 0,
  facility_rating: 0,
  owner_rating: 0,
};

export default function ReviewModal({ open, booking, onClose, onSuccess }) {
  const [ratings, setRatings] = useState(EMPTY);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open || !booking) return null;

  const setCat = (key, val) => setRatings((r) => ({ ...r, [key]: val }));

  const allRated = RATING_CATEGORIES.every((c) => ratings[c.key] >= 1);

  const submit = async () => {
    if (!allRated) {
      toast.error("Mohon isi semua rating (1–5 bintang).");
      return;
    }
    setSubmitting(true);
    try {
      await apiClient.post("/reviews", {
        booking_id: booking.id,
        property_id: booking.property_id,
        ...ratings,
        comment: comment.trim() || null,
      });
      setDone(true);
      toast.success("Review berhasil dikirim. Terima kasih!");
      onSuccess?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => {
    setRatings(EMPTY);
    setComment("");
    setDone(false);
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4" data-testid="review-modal">
      <div className="w-full max-w-lg rounded-2xl bg-card border border-border shadow-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 bg-card">
          <h2 className="font-heading text-lg font-bold">Berikan Review</h2>
          <button onClick={close} data-testid="review-modal-close" className="text-muted-foreground hover:text-foreground">
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="review-modal-success">
            <span className="grid place-items-center w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 mx-auto mb-4">
              <CheckCircle2 size={28} />
            </span>
            <h3 className="font-heading text-lg font-semibold">Review Terkirim</h3>
            <p className="mt-2 text-sm text-muted-foreground">Ulasanmu membantu penghuni lain memilih hunian terbaik.</p>
            <button onClick={close} data-testid="review-modal-done"
              className="mt-5 px-5 py-2.5 rounded-full text-sm font-semibold bg-primary text-primary-foreground hover:scale-105 active:scale-95 transition-transform">
              Selesai
            </button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <p className="text-sm text-muted-foreground">
              {booking.property_name || "Hunian"} · Kamar {booking.room_number}
            </p>
            {RATING_CATEGORIES.map((c) => (
              <div key={c.key} className="flex items-center justify-between gap-3">
                <span className="text-sm font-medium">{c.label}</span>
                <StarRating value={ratings[c.key]} onChange={(v) => setCat(c.key, v)} testid={`review-rating-${c.key}`} />
              </div>
            ))}

            <div>
              <label className="text-sm font-medium">Komentar (opsional)</label>
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                maxLength={2000}
                rows={4}
                data-testid="review-comment-input"
                placeholder="Ceritakan pengalamanmu tinggal di sini…"
                className="mt-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
              />
              <p className="mt-1 text-xs text-muted-foreground text-right">{comment.length}/2000</p>
            </div>

            <button onClick={submit} disabled={submitting || !allRated}
              data-testid="review-submit-button"
              className="w-full py-3 rounded-full text-sm font-semibold bg-accent text-accent-foreground hover:scale-[1.02] active:scale-95 transition-transform disabled:opacity-60 disabled:hover:scale-100 inline-flex items-center justify-center gap-2">
              {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Kirim Review
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
